// Mass import admin page (Phase 1 + Phase 2).
//
// Two upload modes, selectable via the tab strip at the top:
//   - "Manifest CSV" — Phase 1. Parse a fixed-shape CSV; the
//     dispatcher fans out URL fetches across worker goroutines.
//   - "Tarball"      — Phase 2. The operator picks a target
//     (project, repo, format) and uploads a .tar / .tar.gz / .tgz.
//     Every regular file in the archive becomes one artifact at
//     that path inside the target repo.
//
// Below the upload form: a unified list of past + running
// bulk-import jobs with an expandable per-row drilldown. Both modes
// write into the same bulk_imports / bulk_import_artifacts tables,
// so the list shows them side-by-side; the row's `mode` badge marks
// which kind of job it was.
//
// Visually mirrors the JFrog AdminImports layout (same Card / Badge
// / status-icon idiom) so the two import flavours feel like sibling
// surfaces, not bolt-ons.

import { useState } from 'react';
import {
  Archive, Boxes, ChevronDown, ChevronRight, Clock, CheckCircle2,
  FileText, RotateCw, Square, Upload, XCircle, AlertTriangle,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { ProgressBar } from '../../components/ui/ProgressBar';
import {
  useBulkImports,
  useBulkImport,
  startBulkImport,
  startBulkTarballImport,
  cancelBulkImport,
  retryBulkFailed,
  type BulkImportJob,
  type BulkImportStatus,
  type BulkRowState,
} from '../../api/bulkImports';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n < 1024 ** 4) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  return `${(n / 1024 ** 4).toFixed(2)} TB`;
}

function statusTone(s: BulkImportStatus): 'success' | 'warning' | 'danger' | 'neutral' | 'accent' {
  switch (s) {
    case 'completed': return 'success';
    case 'running': return 'accent';
    case 'pending': return 'warning';
    case 'failed': return 'danger';
    case 'cancelled': return 'neutral';
  }
}

function StatusIcon({ status }: { readonly status: BulkImportStatus }) {
  switch (status) {
    case 'completed': return <CheckCircle2 className="h-3 w-3" />;
    case 'failed': return <XCircle className="h-3 w-3" />;
    case 'running': return <RotateCw className="h-3 w-3 animate-spin" />;
    default: return <Clock className="h-3 w-3" />;
  }
}

function rowTone(s: BulkRowState): 'success' | 'warning' | 'danger' | 'neutral' | 'accent' {
  switch (s) {
    case 'completed': return 'success';
    case 'running': return 'accent';
    case 'queued': return 'warning';
    case 'failed': return 'danger';
    case 'skipped': return 'neutral';
  }
}

function progressTone(s: BulkImportStatus): 'accent' | 'success' | 'warning' | 'danger' {
  switch (s) {
    case 'completed': return 'success';
    case 'failed': return 'danger';
    case 'cancelled': return 'warning';
    case 'pending':
    case 'running':
      return 'accent';
  }
}

// etaFromJob estimates time-to-completion from observed throughput.
// We measure rows/sec since the job started; if the rate is too small
// to be meaningful (< ~0.05 rows/s) or the job is already terminal we
// return null so the ProgressBar's eta slot stays empty.
function etaFromJob(job: BulkImportJob): string | null {
  if (job.status !== 'pending' && job.status !== 'running') return null;
  const total = job.rows_total;
  const processed = job.rows_done + job.rows_failed + job.rows_skipped;
  if (total <= 0 || processed <= 0 || processed >= total) return null;
  const startedMs = new Date(job.started_at).getTime();
  if (Number.isNaN(startedMs)) return null;
  const elapsedSec = Math.max(1, (Date.now() - startedMs) / 1000);
  const rate = processed / elapsedSec;
  if (rate < 0.05) return null;
  const remainingSec = Math.round((total - processed) / rate);
  if (remainingSec < 60) return `~${remainingSec}s remaining`;
  if (remainingSec < 3600) return `~${Math.round(remainingSec / 60)} min remaining`;
  return `~${Math.round(remainingSec / 3600)} h remaining`;
}

type ImportMode = 'csv' | 'tarball';

// Backward-compat wrapper — see AdminImportsHub for the consolidated
// IA. App.tsx redirects /admin/imports/bulk to
// /admin/imports?tab=bulk; this export remains for any direct
// importers.
export function AdminBulkImports() {
  return (
    <AdminShell
      title="Mass import"
      description="Two flavours: a CSV manifest of upstream URLs (parallel-fetched in the background), or a tarball whose contents become artifacts in a target repo. Both modes write to the same job tracker; the list below shows everything in one place."
    >
      <AdminBulkImportsBody />
    </AdminShell>
  );
}

// Body for the Imports hub — no <AdminShell> wrapper.
export function AdminBulkImportsBody() {
  const { data: jobs, mutate } = useBulkImports();
  const [mode, setMode] = useState<ImportMode>('csv');
  const [expanded, setExpanded] = useState<string | null>(null);

  const onCancel = async (id: string) => {
    if (!confirm('Cancel this import? In-flight rows finish; queued rows stay un-fetched.')) return;
    try { await cancelBulkImport(id); } catch (e) { alert(String(e)); }
    await mutate();
  };

  const onRetry = async (id: string) => {
    try { await retryBulkFailed(id); } catch (e) { alert(String(e)); }
    await mutate();
  };

  return (
    <>
      <div className="flex items-center gap-2 border-b border-border pb-2">
        <ModeTab
          active={mode === 'csv'}
          onClick={() => setMode('csv')}
          icon={<FileText className="h-4 w-4" />}
          label="Manifest CSV"
        />
        <ModeTab
          active={mode === 'tarball'}
          onClick={() => setMode('tarball')}
          icon={<Archive className="h-4 w-4" />}
          label="Tarball"
        />
      </div>

      {mode === 'csv' ? (
        <CsvUploadCard onSubmitted={mutate} />
      ) : (
        <TarballUploadCard onSubmitted={mutate} />
      )}

      <Card>
        <CardHeader title="Past + running jobs" description="Polls every 5 s while a job is live; stops once everything has terminated." />
        <CardBody>
          {!jobs && <p className="text-sm text-fg-muted">Loading…</p>}
          {jobs?.length === 0 && (
            <p className="text-sm text-fg-muted">No jobs yet. Upload a manifest above to get started.</p>
          )}
          {jobs && jobs.length > 0 && (
            <ul className="divide-y divide-border">
              {jobs.map((j) => (
                <BulkJobRow
                  key={j.id}
                  job={j}
                  expanded={expanded === j.id}
                  onToggle={() => setExpanded((prev) => (prev === j.id ? null : j.id))}
                  onCancel={() => onCancel(j.id)}
                  onRetry={() => onRetry(j.id)}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function ModeTab({
  active, onClick, icon, label,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly icon: React.ReactNode;
  readonly label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ' +
        (active
          ? 'bg-bg-elevated text-fg border border-border'
          : 'text-fg-muted hover:text-fg hover:bg-bg-elevated/60')
      }
      aria-pressed={active}
    >
      {icon}
      {label}
    </button>
  );
}

function CsvUploadCard({ onSubmitted }: { readonly onSubmitted: () => Promise<unknown> }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const onSubmit = async () => {
    if (!file) return;
    setBusy(true); setErr(null); setOkMsg(null);
    try {
      const r = await startBulkImport(file, name || undefined);
      setOkMsg(`Queued ${r.rows_total} rows (job ${r.id.slice(0, 8)}…).`);
      setName('');
      setFile(null);
      const input = document.getElementById('bulk-manifest-input') as HTMLInputElement | null;
      if (input) input.value = '';
      await onSubmitted();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Upload a manifest"
        description="CSV with header: format,project_slug,repo_slug,upstream_url,target_path[,version]. Up to 100 000 rows / 10 MiB. URLs must be reachable from this server."
      />
      <CardBody className="space-y-3">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="job name (optional)"
        />
        <input
          id="bulk-manifest-input"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-md file:border file:border-border file:bg-bg-elevated file:px-3 file:py-1.5 file:text-fg hover:file:border-border-strong"
        />
        {file && (
          <p className="text-xs text-fg-subtle">
            Selected: <span className="text-fg">{file.name}</span> ({fmtBytes(file.size)})
          </p>
        )}
        {err && (
          <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <pre className="whitespace-pre-wrap font-sans">{err}</pre>
          </div>
        )}
        {okMsg && (
          <div className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">
            {okMsg}
          </div>
        )}
      </CardBody>
      <CardFooter className="flex justify-end gap-2">
        <Button
          variant="primary"
          size="md"
          disabled={busy || !file}
          onClick={onSubmit}
        >
          <Upload className="h-4 w-4 mr-2" />
          Start import
        </Button>
      </CardFooter>
    </Card>
  );
}

// Phase-2 tarball upload form. The four required text fields
// (project, repo, format, file) cover the contract enforced by the
// backend's StartTarball: the (project_slug, repo_slug, format)
// triple must resolve to an existing repository, and the file part
// must be a parseable tar / tar.gz / tgz stream.
//
// We do *not* render a project / repo picker yet — typing the slug
// is fine for an admin-only flow and avoids depending on this page
// pulling the org's full project catalogue. Operators almost always
// have the slug ready (they typed it into curl when uploading single
// artifacts in the same target).
function TarballUploadCard({ onSubmitted }: { readonly onSubmitted: () => Promise<unknown> }) {
  const [file, setFile] = useState<File | null>(null);
  const [projectSlug, setProjectSlug] = useState('');
  const [repoSlug, setRepoSlug] = useState('');
  const [format, setFormat] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const canSubmit = !!file && !!projectSlug.trim() && !!repoSlug.trim() && !!format.trim();

  const onSubmit = async () => {
    if (!file) return;
    setBusy(true); setErr(null); setOkMsg(null);
    try {
      const r = await startBulkTarballImport({
        tarball: file,
        projectSlug: projectSlug.trim(),
        repoSlug: repoSlug.trim(),
        format: format.trim().toLowerCase(),
        name: name.trim() || undefined,
      });
      setOkMsg(`Tarball processed (job ${r.id.slice(0, 8)}…). Expand it below to see per-file results.`);
      setFile(null);
      setName('');
      const input = document.getElementById('bulk-tarball-input') as HTMLInputElement | null;
      if (input) input.value = '';
      await onSubmitted();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Upload a tarball"
        description="Pick a target repo + format; every regular file in the archive becomes one artifact at the entry's path. Supports .tar, .tar.gz, .tgz; 2 GiB cap (decompressed); 256 MiB per entry. Symlinks, directories, and absolute / '..' paths are skipped."
      />
      <CardBody className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Input
            value={projectSlug}
            onChange={(e) => setProjectSlug(e.target.value)}
            placeholder="project_slug (e.g. acme)"
          />
          <Input
            value={repoSlug}
            onChange={(e) => setRepoSlug(e.target.value)}
            placeholder="repo_slug (e.g. prod)"
          />
          <Input
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            placeholder="format (e.g. maven, npm, generic)"
          />
        </div>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="job name (optional)"
        />
        <input
          id="bulk-tarball-input"
          type="file"
          accept=".tar,.gz,.tgz,application/gzip,application/x-tar,application/x-gzip"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-md file:border file:border-border file:bg-bg-elevated file:px-3 file:py-1.5 file:text-fg hover:file:border-border-strong"
        />
        {file && (
          <p className="text-xs text-fg-subtle">
            Selected: <span className="text-fg">{file.name}</span> ({fmtBytes(file.size)})
          </p>
        )}
        {busy && (
          <p className="text-xs text-fg-subtle">
            Uploading + extracting; this is synchronous and may take several minutes for a large tarball. Don't close the tab.
          </p>
        )}
        {err && (
          <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <pre className="whitespace-pre-wrap font-sans">{err}</pre>
          </div>
        )}
        {okMsg && (
          <div className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">
            {okMsg}
          </div>
        )}
      </CardBody>
      <CardFooter className="flex justify-end gap-2">
        <Button
          variant="primary"
          size="md"
          disabled={busy || !canSubmit}
          onClick={onSubmit}
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload tarball
        </Button>
      </CardFooter>
    </Card>
  );
}

function BulkJobRow({
  job, expanded, onToggle, onCancel, onRetry,
}: {
  readonly job: BulkImportJob;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly onCancel: () => void;
  readonly onRetry: () => void;
}) {
  const pct =
    job.rows_total > 0
      ? Math.round(((job.rows_done + job.rows_failed + job.rows_skipped) / job.rows_total) * 100)
      : 0;
  const isLive = job.status === 'pending' || job.status === 'running';
  // For a freshly-queued CSV import the dispatcher may not have
  // claimed any rows yet (rows_total stays 0 until the parser flushes
  // the manifest). Render the ProgressBar in indeterminate mode in
  // that pre-claim window so the operator sees activity rather than
  // a stuck 0 % bar.
  const indeterminate = isLive && job.rows_total === 0;
  const eta = etaFromJob(job);
  return (
    <li className="py-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="text-fg-muted hover:text-fg"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {job.mode === 'tarball' ? (
              <Archive className="h-4 w-4 text-fg-subtle" />
            ) : (
              <Boxes className="h-4 w-4 text-fg-subtle" />
            )}
            <span className="font-medium text-fg truncate">{job.name}</span>
            <Badge tone="neutral">
              {job.mode === 'tarball' ? 'tarball' : 'csv'}
            </Badge>
            <Badge tone={statusTone(job.status)}>
              <StatusIcon status={job.status} />
              <span className="ml-1">{job.status}</span>
            </Badge>
          </div>
          <p className="text-xs text-fg-subtle mt-0.5">
            {job.rows_done + job.rows_failed + job.rows_skipped}/{job.rows_total} rows · {fmtBytes(job.bytes_done)} ·
            started {new Date(job.started_at).toLocaleString()}
            {job.started_by ? ` by ${job.started_by}` : ''}
          </p>
          <div className="mt-1.5">
            <ProgressBar
              percent={pct}
              indeterminate={indeterminate}
              tone={progressTone(job.status)}
              eta={eta ?? undefined}
              ariaLabel={`Bulk import ${job.name} progress`}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isLive && (
            <Button variant="subtle" size="sm" onClick={onCancel}>
              <Square className="h-3 w-3 mr-1" />
              Cancel
            </Button>
          )}
          {job.rows_failed > 0 && !isLive && (
            <Button variant="subtle" size="sm" onClick={onRetry}>
              <RotateCw className="h-3 w-3 mr-1" />
              Retry {job.rows_failed} failed
            </Button>
          )}
        </div>
      </div>
      {expanded && <BulkJobDetail jobID={job.id} />}
    </li>
  );
}

function BulkJobDetail({ jobID }: { readonly jobID: string }) {
  const { data } = useBulkImport(jobID);
  if (!data) return <p className="ml-7 mt-3 text-xs text-fg-subtle">Loading rows…</p>;
  if (data.artifacts.length === 0) {
    return <p className="ml-7 mt-3 text-xs text-fg-subtle">No row detail available.</p>;
  }
  return (
    <div className="ml-7 mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-fg-subtle">
            <th className="font-medium pr-3 pb-1">#</th>
            <th className="font-medium pr-3 pb-1">format</th>
            <th className="font-medium pr-3 pb-1">project / repo</th>
            <th className="font-medium pr-3 pb-1">target path</th>
            <th className="font-medium pr-3 pb-1">state</th>
            <th className="font-medium pb-1">size</th>
          </tr>
        </thead>
        <tbody>
          {data.artifacts.map((a) => (
            <tr key={a.id} className="border-t border-border/60">
              <td className="pr-3 py-1 font-mono text-fg-subtle">{a.row_num}</td>
              <td className="pr-3 py-1 text-fg-muted">{a.format}</td>
              <td className="pr-3 py-1 text-fg-muted">
                {a.project_slug}/{a.repo_slug}
              </td>
              <td className="pr-3 py-1 text-fg-muted break-all max-w-[280px]" title={a.target_path}>
                {a.target_path}
              </td>
              <td className="pr-3 py-1">
                <Badge tone={rowTone(a.state)}>{a.state}</Badge>
                {a.error && (
                  <span
                    className="ml-2 text-danger"
                    title={a.error}
                  >
                    {a.error.length > 60 ? a.error.slice(0, 60) + '…' : a.error}
                  </span>
                )}
              </td>
              <td className="py-1 font-mono text-fg-subtle">
                {a.bytes > 0 ? fmtBytes(a.bytes) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
